import React, { createContext, useContext, useEffect, useState } from "react";
import { AuthenticatorAssuranceLevels, Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { clearStoredAuthSession } from "@/lib/authPersistence";
import { ensureProfile, getOnboardingCompletion } from "@/lib/onboardingProfile";

export type AuthRole = "user" | "moderator" | "admin";

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  securityLoading: boolean;
  role: AuthRole;
  isPrivilegedUser: boolean;
  mfaLevel: AuthenticatorAssuranceLevels | null;
  hasVerifiedMfaFactor: boolean;
  requiresMfa: boolean;
  requiresPrivilegedMfaSetup: boolean;
  onboardingComplete: boolean | null; // null = still loading
  refreshProfile: () => Promise<void>;
  refreshSecurity: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  loading: true,
  securityLoading: true,
  role: "user",
  isPrivilegedUser: false,
  mfaLevel: null,
  hasVerifiedMfaFactor: false,
  requiresMfa: false,
  requiresPrivilegedMfaSetup: false,
  onboardingComplete: null,
  refreshProfile: async () => {},
  refreshSecurity: async () => {},
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

function getHighestRole(roles: AuthRole[]): AuthRole {
  if (roles.includes("admin")) return "admin";
  if (roles.includes("moderator")) return "moderator";
  return "user";
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [securityLoading, setSecurityLoading] = useState(true);
  const [role, setRole] = useState<AuthRole>("user");
  const [mfaLevel, setMfaLevel] = useState<AuthenticatorAssuranceLevels | null>(null);
  const [hasVerifiedMfaFactor, setHasVerifiedMfaFactor] = useState(false);
  const [requiresMfa, setRequiresMfa] = useState(false);
  const [requiresPrivilegedMfaSetup, setRequiresPrivilegedMfaSetup] = useState(false);
  const [onboardingComplete, setOnboardingComplete] = useState<boolean | null>(null);

  const checkOnboarding = async (user: User) => {
    await ensureProfile(user);
    const complete = await getOnboardingCompletion(user.id);
    setOnboardingComplete(complete);
  };

  const refreshProfile = async () => {
    if (session?.user) {
      await checkOnboarding(session.user);
    }
  };

  const refreshSecurity = async () => {
    if (!session?.user) {
      setRole("user");
      setRequiresMfa(false);
      setHasVerifiedMfaFactor(false);
      setRequiresPrivilegedMfaSetup(false);
      setMfaLevel(null);
      setSecurityLoading(false);
      return;
    }

    setSecurityLoading(true);
    const [{ data, error }, { data: factorData, error: factorError }, { data: roleRows, error: roleError }] = await Promise.all([
      supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
      supabase.auth.mfa.listFactors(),
      supabase.from("user_roles").select("role").eq("user_id", session.user.id),
    ]);

    const highestRole = roleError
      ? "user"
      : getHighestRole(((roleRows || []) as { role: AuthRole }[]).map((entry) => entry.role));
    const verifiedFactorCount = factorData?.totp.filter((factor) => factor.status === "verified").length ?? 0;

    if (roleError) {
      console.error("Error fetching user role:", roleError);
    }

    if (factorError) {
      console.error("Error fetching MFA factors:", factorError);
    }

    setRole(highestRole);
    setHasVerifiedMfaFactor(verifiedFactorCount > 0);
    setRequiresPrivilegedMfaSetup(
      (highestRole === "admin" || highestRole === "moderator") && verifiedFactorCount === 0,
    );

    if (error) {
      console.error("Error fetching MFA assurance level:", error);
      setRequiresMfa(false);
      setMfaLevel(null);
    } else {
      setMfaLevel(data.currentLevel);
      setRequiresMfa(data.nextLevel === "aal2" && data.currentLevel !== "aal2");
    }

    setSecurityLoading(false);
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session?.user) {
        // Use setTimeout to avoid Supabase deadlock on auth state change
        setTimeout(() => {
          void checkOnboarding(session.user);
        }, 0);
      } else {
        setOnboardingComplete(null);
        setRole("user");
        setRequiresMfa(false);
        setHasVerifiedMfaFactor(false);
        setRequiresPrivilegedMfaSetup(false);
        setMfaLevel(null);
        setSecurityLoading(false);
      }
      setLoading(false);
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session?.user) {
        checkOnboarding(session.user).then(() => setLoading(false));
      } else {
        setLoading(false);
        setSecurityLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    void refreshSecurity();
  }, [session?.user?.id]);

  const signOut = async () => {
    await supabase.auth.signOut();
    await clearStoredAuthSession();
    setOnboardingComplete(null);
    setRole("user");
    setRequiresMfa(false);
    setHasVerifiedMfaFactor(false);
    setRequiresPrivilegedMfaSetup(false);
    setMfaLevel(null);
    setSecurityLoading(false);
  };

  return (
    <AuthContext.Provider value={{
      session,
      user: session?.user ?? null,
      loading,
      securityLoading,
      role,
      isPrivilegedUser: role === "admin" || role === "moderator",
      mfaLevel,
      hasVerifiedMfaFactor,
      requiresMfa,
      requiresPrivilegedMfaSetup,
      onboardingComplete,
      refreshProfile,
      refreshSecurity,
      signOut,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
